/**
 * @fileoverview The one reminder on Today, under the weather: the advice the
 * server placed there (todo.reads.weather; the to-do rebuild, batch 5: which
 * one shows, for whom and how often is the server's, and during the welcome it
 * places only a beta tester's or founding gardener's line there). The first
 * step of an empty garden (reads.empty_garden) decides when the welcome's
 * place step shows; that step is drawn by WelcomePlace. The advice after an
 * action and the bottom panel live where those actions are (the add form,
 * ShareMoments). The advice rules are edited in the backend's Grow settings,
 * in the one rules list.
 *
 * A button is shown only where it would do something here (tipCanRun): a link
 * that is unsafe or goes nowhere, a share button away from the share panel,
 * or an install the browser will not offer, leaves the advice without one;
 * so does Subscribe for a member tending someone else's garden (useTendingFor; B23: the invitee only uses the inviter's plan).
 * Its link is drawn as a real link (LinkButton).
 *
 * Where: TopTip on Today (TodayPage), under the weather.
 * API: none of its own; seen, put away and done go through TipsContext
 * (POST /todo/marks).
 * Settings: the advice rules (Grow settings); Chrome's install offer
 * (useInstallPrompt).
 * Words (en/es): the advice's own (the server's), tips.close, tips.notNow,
 * tips.gotIt, welcome.phone.androidManual.
 * Watch out: weekly advice's button puts it away (back in a week), not done;
 * every other piece's button finishes it.
 *
 * @module tips/TipViews
 */

import React from 'react';
import { X } from 'lucide-react';
import { Button, type ButtonProps } from '@/shared/components/ui/button';
import { LinkButton } from '@/shared/components/LinkButton';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { readLink } from '@/shared/lib/safeLink';
import { useInstallPrompt } from '@/features/welcome/useInstallPrompt';
import { useTips } from './useTips';
import { tipCanRun, useTipAction, type TipContext } from './useTipAction';
import { useTendingFor } from '@/features/billing/lib/useBilling';
import type { Advice } from './types';

/**
 * Says the advice was seen (once a day) while it is on screen.
 *
 * @param advice - The advice on screen, or null.
 */
export function useSeen(advice: Advice | null): void {
  const { mark } = useTips();
  React.useEffect(() => { if (advice) mark(advice, 'seen'); }, [advice, mark]);
}

/** A piece of advice's button: what it says, what the screen knows, and what else pressing it does. */
interface TipButtonProps extends Pick<ButtonProps, 'size' | 'className'> {
  /** The advice whose button it is. */
  advice: Advice;
  /** What it says. */
  label: string;
  /** What the screen knows (the add form). */
  context?: TipContext;
  /** Run as it is pressed, after the advice's own action (marked done or put away). */
  onPressed?: () => void;
}

/**
 * A piece of advice's button: a link for its own link, so it opens like one,
 * and a button for the rest. Shown only where tipCanRun says it does something.
 *
 * @param props - TipButtonProps.
 * @returns The button or link.
 */
export const TipButton: React.FC<TipButtonProps> = ({ advice, label, context, onPressed, ...looks }) => {
  const run = useTipAction();
  const link = advice.button?.does === 'open_link' ? readLink(advice.button.url) : null;
  if (link) return <LinkButton link={link} onClick={onPressed} {...looks}>{label}</LinkButton>;
  return <Button {...looks} onClick={() => { run(advice, context); onPressed?.(); }}>{label}</Button>;
};

/**
 * One reminder on Today, under the weather: the advice the server placed
 * there, with its button and a way to put it away; nothing when it placed none.
 *
 * @returns The reminder, or null.
 */
export const TopTip: React.FC = () => {
  const tips = useTips();
  const { reactTranslate: t } = useTranslation();
  const { canPrompt } = useInstallPrompt();
  /** Someone else's garden: no Subscribe button (B23). */
  const tending = useTendingFor() !== null;
  const advice = tips.read('weather');
  useSeen(advice);
  if (!advice) return null;
  const words = tips.words(advice);
  // Chrome has not offered an install (yet, or any more): say where it is instead.
  const installBlocked = advice.button?.does === 'install' && !canPrompt;
  // Only a button that does something here: Today has no add form or plant for it.
  const button = words.button && tipCanRun(advice, { canInstall: canPrompt, tending }) ? words.button : null;
  /** Puts the advice away (for good, or a week for weekly advice: the server's). */
  const dismiss = (): void => tips.mark(advice, 'put_away');

  return (
    <section className="relative rounded-lg border bg-card p-4 pr-10">
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('tips.close')}
        className="absolute right-1 top-1 rounded-sm p-2.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
      {words.title && <h2 className="font-semibold leading-snug font-body">{words.title}</h2>}
      {words.body && <p className="mt-1 text-sm text-muted-foreground">{words.body}</p>}
      {installBlocked && <p className="mt-1 text-sm text-muted-foreground">{t('welcome.phone.androidManual')}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {button ? (
          <>
            {/* What the button marks is the server's (buttonMark): weekly advice is put away and comes back next week, the rest is done. */}
            <TipButton advice={advice} label={button} size="sm" onPressed={() => tips.mark(advice, advice.buttonMark)} />
            <Button size="sm" variant="outline" onClick={dismiss}>{t('tips.notNow')}</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={dismiss}>{t('tips.gotIt')}</Button>
        )}
      </div>
    </section>
  );
};
